import React, {
  useState, useEffect, useRef, useCallback, memo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { formatInTz } from '../../utils/timezone';
import { faqAPI, agentAPI } from '../../services/api';
import {
  getOrCreateSessionId,
  resetSessionId,
  sendTicketChatMessage,
  DEFAULT_WEBHOOK_URL,
} from '../../services/ticketChatApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(date) {
  return formatInTz(date, { hour: '2-digit', minute: '2-digit', hour12: true });
}

const CHAT_STORAGE_KEY = 'ai_chat_history';
const THEME_STORAGE_KEY = 'ai_chat_theme';

function loadHistory(storageKey = CHAT_STORAGE_KEY) {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHistory(messages, storageKey = CHAT_STORAGE_KEY) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(messages.slice(-100)));
  } catch {}
}

function loadTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

const REQUESTER_SUGGESTIONS = [
  { label: '🔑 Reset my password', message: 'I forgot my password — how do I reset it?' },
  { label: '📧 Add user to email group', message: 'Add a user to an email group' },
  { label: '🌐 VPN access request', message: 'I need VPN access for remote work' },
  { label: '📋 Check approval status', message: 'Check my approval request status' },
  { label: '🔍 IT help / knowledge base', message: 'Help me with a common IT issue' },
];

export const REQUESTER_CHAT_CONFIG = {
  storageKey: 'requester_ai_chat_history',
  sessionKey: 'requester_ai_chat_session_id',
  title: 'AI Assistant',
  subtitle: 'Online · Support Portal',
  emptySubtitle: 'Ask about M365 requests, VPN, groups, or IT help',
  placeholder: 'Describe what you need… (Shift+Enter for new line)',
  suggestions: REQUESTER_SUGGESTIONS,
  useBackendAgent: true,
  useFaq: false,
};

const TICKET_SUGGESTIONS = [
  { label: '📊 Overall ticket status today', message: 'Give me an overview of all ticket status counts today — open, pending, resolved, and closed.' },
  { label: '🔴 How many urgent tickets are open?', message: 'How many urgent priority tickets are currently open and unresolved?' },
  { label: '👥 Agent performance this month', message: 'Summarize agent performance this month — who has resolved the most tickets?' },
  { label: '⚠️ SLA breached tickets', message: 'List all tickets that have breached SLA and need immediate attention.' },
  { label: '📈 Work progress this week', message: 'How is the team work progressing this week? Show ticket trends and resolution rate.' },
  { label: '🏢 Department-wise ticket load', message: 'Which department has the highest number of open tickets right now?' },
];

const PASSWORD_RESET_SUGGESTIONS = [
  { label: '🔑 Reset my password', message: 'I forgot my password and need to reset it. How do I proceed?' },
  { label: '📧 Verify company email', message: 'How do I verify my company email for password reset?' },
  { label: '⏱ How long does reset take?', message: 'How long does the password reset process usually take?' },
  { label: '🆘 Locked out of account', message: 'I am locked out of my account and cannot sign in. What should I do?' },
];

export const PASSWORD_RESET_CHAT_CONFIG = {
  webhookUrl: 'https://apilive.victione.com:5678/webhook/password-reset',
  storageKey: 'password_reset_chat_history',
  sessionKey: 'password_reset_chat_session_id',
  title: 'Password Reset Assistant',
  subtitle: 'Online · Secure account help',
  emptyTitle: 'Need help signing in?',
  emptySubtitle: 'I can guide you through password reset and account recovery',
  placeholder: 'Describe your sign-in issue… (Shift+Enter for new line)',
  useFaq: false,
  suggestions: PASSWORD_RESET_SUGGESTIONS,
  avatarEmoji: '🔐',
  accentGradient: 'from-sky-500 to-indigo-600',
};

/** n8n ticket-chat webhook config for agent dashboard & floating ✦ button */
export const TICKET_CHAT_CONFIG = {
  webhookUrl: DEFAULT_WEBHOOK_URL,
  useBackendAgent: false,
  useFaq: false,
};

// ─── Typing dots ──────────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-0.5 py-1">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
          className="h-1.5 w-1.5 rounded-full bg-indigo-500 dark:bg-indigo-400"
        />
      ))}
    </div>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={handle}
      title="Copy message"
      className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] transition-colors ${
        copied ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
      }`}
    >
      {copied ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20,6 9,17 4,12" /></svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
      )}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────────

function MarkdownContent({ content }) {
  return (
    <div className="ai-markdown text-[13.5px] leading-relaxed">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          code: ({ inline, children }) => (inline ? (
            <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-indigo-600 dark:bg-slate-700 dark:text-indigo-300">{children}</code>
          ) : (
            <pre className="my-2 overflow-x-auto rounded-lg bg-slate-900 p-3 font-mono text-xs text-slate-200"><code>{children}</code></pre>
          )),
          ul: ({ children }) => <ul className="my-1 list-disc pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="my-1 list-decimal pl-5">{children}</ol>,
          li: ({ children }) => <li className="mb-0.5">{children}</li>,
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline dark:text-indigo-400">{children}</a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-[3px] border-indigo-500 pl-3 italic text-slate-600 dark:text-slate-400">{children}</blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// ─── Single message bubble ────────────────────────────────────────────────────

const MessageBubble = memo(({ msg }) => {
  const isUser = msg.role === 'user';
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`mb-1.5 flex items-end gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-md ${
        isUser ? 'bg-indigo-600' : 'bg-gradient-to-br from-indigo-500 to-violet-500'
      }`}
      >
        {isUser ? 'U' : '✦'}
      </div>

      <div className={`flex max-w-[72%] flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`px-3.5 py-2.5 text-[13.5px] leading-relaxed shadow-sm ${
          isUser
            ? 'rounded-[18px_18px_4px_18px] bg-gradient-to-br from-indigo-600 to-indigo-500 text-white shadow-indigo-500/30'
            : msg.isError
              ? 'rounded-[18px_18px_18px_4px] border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300'
              : 'rounded-[18px_18px_18px_4px] border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'
        }`}
        >
          {isUser ? <span className="break-words">{msg.content}</span> : <MarkdownContent content={msg.content} />}
        </div>

        <div className={`flex items-center gap-1.5 transition-opacity ${hovered ? 'opacity-100' : 'opacity-0'} ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          <span className="text-[10.5px] text-slate-400">{formatTime(new Date(msg.timestamp))}</span>
          <CopyBtn text={msg.content} />
        </div>
      </div>
    </motion.div>
  );
});

// ─── Theme toggle ─────────────────────────────────────────────────────────────

function ThemeToggle({ theme, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>
      )}
    </button>
  );
}

// ─── Main Chatbot Component ───────────────────────────────────────────────────

export default function AIChatbot({
  webhookUrl = DEFAULT_WEBHOOK_URL,
  storageKey = CHAT_STORAGE_KEY,
  sessionKey = 'ai_chat_session_id',
  title = 'AI Assistant',
  subtitle = 'Online · Powered by PowerDesk AI',
  emptyTitle,
  emptySubtitle = 'Ask me anything about your tickets and service requests',
  placeholder = 'Ask me about your tickets… (Shift+Enter for new line)',
  useFaq = true,
  useBackendAgent = false,
  suggestions = TICKET_SUGGESTIONS,
  avatarEmoji = '✦',
  accentGradient = 'from-indigo-600 to-indigo-500',
  prefilledEmail = '',
  displayName = '',
}) {
  const greetingName = String(displayName || '').trim();
  const resolvedEmptyTitle = emptyTitle ?? (
    greetingName ? `Hi ${greetingName}, how can I help you?` : 'How can I help you?'
  );
  const [messages, setMessages] = useState(() => loadHistory(storageKey));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(() => getOrCreateSessionId(sessionKey));
  const [theme, setTheme] = useState(loadTheme);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { saveHistory(messages, storageKey); }, [messages, storageKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  };

  const sendMessage = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError('');

    try {
      let answer;
      let kbResults = [];

      if (useBackendAgent) {
        const data = await agentAPI.chat(text, sessionId, 'chat');
        answer = data.answer || data.response || data.message || 'Sorry, I could not understand that.';
      } else {
        let kbContext = '';
        if (useFaq) {
          try {
            kbResults = await faqAPI.search(text, 3);
            if (kbResults.length > 0) {
              kbContext = kbResults
                .map((f, i) => `[KB${i + 1}] Q: ${f.question}\nA: ${f.answer}`)
                .join('\n\n');
            }
          } catch (_) { /* FAQ search failure should not block AI */ }
        }

        const enrichedMessage = kbContext
          ? `[Knowledge Base Context - use this to answer if relevant]\n${kbContext}\n\n[User Question]\n${text}`
          : text;

        const emailHint = (prefilledEmail || '').trim().toLowerCase();
        answer = await sendTicketChatMessage({
          message: enrichedMessage,
          sessionId,
          webhookUrl,
          extraBody: {
            original_message: text,
            ...(emailHint ? { company_email: emailHint, email: emailHint } : {}),
            ...(useFaq && kbResults.length
              ? { kb_results: kbResults.map((f) => ({ question: f.question, category: f.category_name })) }
              : {}),
          },
        });
      }

      const sourceNote = !useBackendAgent && kbResults.length > 0
        ? `\n\n---\n*Sources from Knowledge Base: ${kbResults.map((f) => `"${f.question.slice(0, 50)}..."`).join(', ')}*`
        : '';

      const aiMsg = {
        id: Date.now() + 1,
        role: 'ai',
        content: answer + sourceNote,
        timestamp: new Date().toISOString(),
        kbResults,
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      setError('Failed to reach AI. Please try again.');
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        role: 'ai',
        content: '⚠️ I\'m having trouble connecting right now. Please try again in a moment.',
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [input, loading, sessionId, webhookUrl, useFaq, useBackendAgent, prefilledEmail]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(undefined);
    }
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(storageKey);
    setError('');
    if (useBackendAgent) {
      agentAPI.clearSession(sessionId).catch(() => {});
    } else {
      setSessionId(resetSessionId(sessionKey));
    }
  };

  return (
    <div className={`flex h-full flex-col overflow-hidden font-sans ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="flex h-full flex-col bg-slate-100 dark:bg-slate-900">

        {/* Header */}
        <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accentGradient} text-lg shadow-lg shadow-indigo-500/30`}>
              {avatarEmoji}
            </div>
            <div>
              <div className="text-[15px] font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
              <div className="flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_#22c55e]" />
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{subtitle}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            <button
              type="button"
              onClick={clearChat}
              title="Clear conversation"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-red-700 dark:hover:bg-red-950/50 dark:hover:text-red-400"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3,6 5,6 21,6" /><path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2" /></svg>
              Clear chat
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex flex-1 flex-col overflow-y-auto px-5 pb-2 pt-5">

          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-1 flex-col items-center justify-start gap-4 pb-5 pt-5"
            >
              <div className={`flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-gradient-to-br ${accentGradient} text-[32px] shadow-xl shadow-indigo-500/30`}>
                {avatarEmoji}
              </div>
              <div className="text-center">
                <div className="text-[22px] font-extrabold tracking-tight text-slate-900 dark:text-white">{resolvedEmptyTitle}</div>
                <div className="mt-1.5 text-[13.5px] text-slate-500 dark:text-slate-400">{emptySubtitle}</div>
              </div>

              <div className="mt-2 flex w-full max-w-[480px] flex-col gap-2">
                {suggestions.map((s) => (
                  <motion.button
                    key={s.label}
                    type="button"
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => sendMessage(s.message)}
                    className="flex cursor-pointer items-center justify-between gap-2.5 rounded-xl border-[1.5px] border-slate-200 bg-white px-4 py-2.5 text-left text-[13px] font-semibold text-slate-800 shadow-sm transition-colors hover:border-indigo-500 hover:bg-indigo-50/50 hover:shadow-md dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/30"
                  >
                    <span>{s.label}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B5BDB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22,2 15,22 11,13 2,9" />
                    </svg>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}
          </AnimatePresence>

          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18 }}
                className="mb-1.5 flex items-end gap-2.5"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-sm font-bold text-white">
                  ✦
                </div>
                <div className="rounded-[18px_18px_18px_4px] border border-slate-200 bg-white px-4 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                  <TypingDots />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-2 flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-3.5 py-2 text-xs text-red-600 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400"
              >
                <span>⚠</span> {error}
                <button type="button" onClick={() => setError('')} className="ml-auto border-none bg-transparent text-sm font-bold text-red-600 dark:text-red-400">✕</button>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-5 pb-4 pt-3.5 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-2 flex items-center gap-1 text-[10.5px] text-slate-400">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
            Session: <span className="font-mono text-slate-500 dark:text-slate-400">{sessionId.slice(0, 18)}…</span>
          </div>

          <div className="flex items-end gap-2.5 rounded-xl border-[1.5px] border-slate-200 bg-slate-50 px-3.5 py-2.5 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-900">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              className="max-h-[120px] min-h-[24px] flex-1 resize-none border-none bg-transparent text-[13.5px] leading-relaxed text-slate-900 outline-none dark:text-slate-100 dark:placeholder:text-slate-500"
            />

            <button
              type="button"
              onClick={() => sendMessage(undefined)}
              disabled={!input.trim() || loading}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border-none transition-all ${
                input.trim() && !loading
                  ? 'cursor-pointer bg-gradient-to-br from-indigo-600 to-indigo-500 shadow-lg shadow-indigo-500/35'
                  : 'cursor-default bg-slate-200 dark:bg-slate-700'
              }`}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="h-4 w-4 rounded-full border-2 border-indigo-500 border-t-transparent"
                />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={input.trim() ? '#FFFFFF' : '#A0AEC0'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22,2 15,22 11,13 2,9" />
                </svg>
              )}
            </button>
          </div>

          <div className="mt-2 text-center text-[11px] text-slate-400">
            Press <kbd className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] dark:border-slate-600 dark:bg-slate-700">Enter</kbd> to send · <kbd className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] dark:border-slate-600 dark:bg-slate-700">Shift+Enter</kbd> for new line
          </div>
        </div>
      </div>
    </div>
  );
}
